import * as DataV2 from './archibus-v2/DataApi'
import {
  isToday,
  combineDateAndTimeStrings,
  formatDate,
  formatTime
} from '../utils/time'
import * as AbWorkflow from './util/workflow'
import * as Document from './archibus/DocumentApi'
import * as Data from './archibus/DataApi'
import {sqlDateToDate} from '../utils/helpers'
import {fetchWorkspaceReservations} from './workspace'
import * as ReactGA from 'react-ga'
import {parseTime} from '../utils/time'

const getRoomPhoto = item => {
  const {rmRmPhoto, blId, flId, rmId} = item
  if (rmRmPhoto) {
    return Document.getTempDocumentUrl(
      'rm',
      {
        bl_id: blId,
        fl_id: flId,
        rm_id: rmId
      },
      'rm_photo'
    ).then(url => {
      return Promise.resolve({...item, url})
    })
  } else {
    return Promise.resolve({...item, url: null})
  }
}

export const fetchConferenceRoomReservations = ({blId, flId, rmId}) => {
  let reservations = []

  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', blId)
  restriction.addClause('rm.fl_id', flId)
  restriction.addClause('rm.rm_id', rmId)

  return DataV2.getDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'conferenceRoomReservations',
    restriction
  ).then(result => {
    const {data} = result
    const currentTime = new Date().getTime()
    if (data.length > 0) {
      reservations = data.filter(item => {
        const {reserveDateStart, reserveDateEnd, reserveTimeEnd} = item
        const endTime = combineDateAndTimeStrings(
          reserveDateEnd,
          reserveTimeEnd
        )
        return isToday(reserveDateStart) && currentTime < endTime.getTime()
      })
      return Promise.resolve(reservations)
    } else {
      return Promise.resolve(reservations)
    }
  })
}

const fetchRoomInfoForFloor = ({blId, flId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', blId)
  restriction.addClause('rm.fl_id', flId)
  restriction.addClause('rm.reservable', 1)
  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'rooms',
    restriction
  ).then(result => {
    const {data} = result
    let indexedRooms = data.reduce((acc, room) => {
      acc[room.roomCode] = room
      return acc
    }, {})
    return Promise.resolve(indexedRooms)
  })
}

const getRoomPhotoUrls = availableRooms => {
  let roomsWithPhotos = availableRooms.filter(room => room.rmPhoto)

  return Promise.all(
    roomsWithPhotos.map(room => {
      const {bl_id, fl_id, rm_id} = room
      return Document.getTempDocumentUrl(
        'rm',
        {bl_id, fl_id, rm_id},
        'rm_photo'
      ).then(url => Promise.resolve({key: room.roomCode, url}))
    })
  ).then(roomsWithUrls => {
    let roomPhotoIndex = roomsWithUrls.reduce((acc, room) => {
      acc[room.key] = room
      return acc
    }, {})

    return Promise.resolve(roomPhotoIndex)
  })
}
export const fetchAvailableRoomsByFloorWithRoomInfo = params => {
  const {bl_id: blId, fl_id: flId} = params

  let availableRooms = []

  return AbWorkflow.callMethod(
    'AbEssentialFacility-ReservationKioskService-searchAvailableReservationRooms',
    [params]
  )
    .then(result => {
      const {code, data} = result
      if (code === 'executed') {
        availableRooms = data
        return fetchRoomInfoForFloor({blId, flId})
      } else {
        return Promise.reject(result.message)
      }
    })
    .then(indexedRooms => {
      // Add the room info to the available rooms
      let availableRoomsWithRoomInfo = availableRooms.map(availableRoom => {
        const {bl_id, fl_id, rm_id} = availableRoom
        let roomIndex = `${bl_id};${fl_id};${rm_id}`
        const room = indexedRooms[roomIndex]
        let roomData = {
          rmPhoto: room.rmPhoto || null,
          rmName: room.name || null,
          roomCode: roomIndex
        }
        return {...availableRoom, ...roomData}
      })

      availableRooms = availableRoomsWithRoomInfo
      return getRoomPhotoUrls(availableRoomsWithRoomInfo)
    })
    .then(roomsWithPhotos => {
      let availableRoomsWithPhotos = availableRooms.map(room => {
        room.url = roomsWithPhotos[room.roomCode]
          ? roomsWithPhotos[room.roomCode].url
          : null

        return room
      })
      return Promise.resolve(availableRoomsWithPhotos)
    })
}

/**
 * Adds the current date and current time restriction to the Ab.View.Restriction object
 * @param {Ab.View.Restriciton} restriction The existing datasource restriction. Passed as a reference
 * to allow the restriction clause to be added directly to the existing restriction.
 * @returns {Ab.View.Restriction}
 */
const addReservationTimeRestriction = ({restriction}) => {
  let now = new Date()
  let currentDate = formatDate(now, 'YYYY-MM-DD')
  let currentTime = formatTime(now, 'HH:mm')
  restriction.addClause('reserve.date_start', currentDate, '>', ')AND(')
  restriction.addClause('reserve.date_start', currentDate, '=', ')OR(')
  restriction.addClause('reserve.time_end', currentTime, '>', 'AND')

  return restriction
}

export const fetchMeetingRoomReservations = ({
  employee,
  numberOfRecords = 1000
}) => {
  let restriction = new Ab.view.Restriction()
  restriction = addReservationTimeRestriction({restriction})

  return DataV2.getPagedDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'roomReservations',
    restriction,
    [{name: 'employeeId', value: employee}],
    1,
    numberOfRecords
  ).then(reservations => {
    return Promise.resolve(reservations)
  })
}

export const meetingRoomCheckInOut = ({
  reserveUserRequestedFor,
  reserveResId,
  rmresId,
  verified
}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('reserve_rm.rmres_id', rmresId)

  return DataV2.getDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'roomReservationUpdate',
    restriction
  ).then(result => {
    const {data} = result
    if (data.length > 0) {
      const record = new Ab.data.Record()
      record.isNew = false

      record.setOldValue('reserve_rm.rmres_id', rmresId)
      record.setValue('reserve_rm.rmres_id', rmresId)

      // TODO: reservation.verified values may be '0' and '1', or 0 and 1, depending on the caller

      record.setOldValue('reserve_rm.verified', verified === 1 ? '0' : '1')
      record.setValue('reserve_rm.verified', verified === 1 ? '1' : '0')
      record.setValue(
        'reserve_rm.user_last_modified_by',
        reserveUserRequestedFor
      )

      // on check-out, change the end time of the reservation
      if (verified === 0) {
        const timeEnd = formatDate(new Date(), 'HH:mm')
        record.setOldValue('reserve_rm.time_end', '00:00.00.000')
        record.setValue('reserve_rm.time_end', timeEnd + '.00.000')

        const reservation = new Ab.data.Record()
        reservation.isNew = false
        reservation.setOldValue('reserve.res_id', reserveResId)
        reservation.setValue('reserve.res_id', reserveResId)
        reservation.setOldValue('reserve.time_end', '00:00.00.000')
        reservation.setValue('reserve.time_end', timeEnd + '.00.000')

        Data.updateDataRecord(
          'ab-selfservice-dashboard.axvw',
          'reservationUpdate',
          reservation
        )
      }

      Data.updateDataRecord(
        'ab-selfservice-dashboard.axvw',
        'roomReservationUpdate',
        record
      )

      return Promise.resolve()
    } else {
      return Promise.reject('Meeting check-in error')
    }
  })
}

export const fetchRoomArrange = async ({blId, flId, rmId}) => {
  let restriction = new Ab.view.Restriction()

  restriction.addClause('rm_arrange.bl_id', blId)
  restriction.addClause('rm_arrange.fl_id', flId)
  restriction.addClause('rm_arrange.rm_id', rmId)

  const {data} = await DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'rmArrange',
    restriction
  )

  let [room] = data
  // Handle the case where the rm_arrange record does not exist
  if (!room) {
    room.dayStart = null
    room.dayEnd = null
    room.postBlock = 0
  }
  return room
}

const copyValues = (source, record) => {
  return Object.keys(source.getValues()).reduce((acc, fieldName) => {
    let value = source.getValue(fieldName)
    acc.setValue(fieldName, value)
    return acc
  }, record)
}

export const saveRoomReservation = ({meeting, recurrence}) => {
  const attendeeEmails = meeting.attendees.reduce(
    (acc, item) => `${acc};${item.email}`,
    ''
  )

  let reservation = new Ab.data.Record()
  reservation.isNew = meeting.isNew

  // correct start and end date to ISO format strings
  const [firstDate] = meeting.dateList
  let startDate = firstDate || meeting.startDate
  startDate = formatDate(startDate, 'YYYY-MM-DD')



  // take the last date in the list, when editing recurring meeting
  const dateListLength = meeting.dateList.length
  let endDate =
    dateListLength > 0 ? meeting.dateList[dateListLength - 1] : meeting.endDate

  endDate = formatDate(endDate, 'YYYY-MM-DD')

  reservation.setValue('reserve.res_id', meeting.resId)
  if (meeting.recurringRule !== '' && meeting.resParent !== '') {
    reservation.setValue('reserve.res_parent', meeting.resParent)
  }
  reservation.setValue('reserve.reservation_name', meeting.name)
  reservation.setValue('reserve.date_start', startDate)
  reservation.setValue('reserve.date_end', endDate)
  reservation.setValue('reserve.time_start', meeting.startTime + '.00.000')
  reservation.setValue('reserve.time_end', meeting.endTime + '.00.000')
  reservation.setValue('reserve.user_created_by', meeting.requestor.em_id)
  if (!meeting.isNew) {
    reservation.setValue(
      'reserve.user_last_modified_by',
      meeting.requestor.em_id
    )
  }
  reservation.setValue('reserve.user_requested_by', meeting.requestor.em_id)
  reservation.setValue('reserve.email', meeting.requestor.email)
  reservation.setValue('reserve.phone', meeting.requestor.phone)
  reservation.setValue('reserve.user_requested_for', meeting.organizer.em_id)

  reservation.setValue('reserve.dv_id', meeting.department.dvId)
  reservation.setValue('reserve.dp_id', meeting.department.dpId)
  reservation.setValue('reserve.recurring_rule', meeting.recurrenceXml)
  reservation.setValue(
    'reserve.res_type',
    meeting.recurringRule !== '' ? 'recurring' : 'regular'
  )
  reservation.setValue(
    'reserve.attendees',
    attendeeEmails.length > 0 ? attendeeEmails.substr(1) : ''
  )
  reservation.setValue('reserve.meeting_private', meeting.isPrivate ? '1' : '0')
  reservation.setValue('reserve.outlook_unique_id', meeting.outlookUniqueId)
  reservation.setValue('reserve.comments', meeting.comments)
  reservation.setValue('reserve.status', meeting.status)

  let roomAllocation = new Ab.data.Record()

  roomAllocation.isNew = meeting.isNew
  roomAllocation.setValue('reserve_rm.rmres_id', meeting.rmResId)
  roomAllocation.setValue('reserve_rm.res_id', meeting.resId)
  roomAllocation.setValue('reserve_rm.bl_id', meeting.room.blId)
  roomAllocation.setValue('reserve_rm.fl_id', meeting.room.flId)
  roomAllocation.setValue('reserve_rm.rm_id', meeting.room.rmId)
  roomAllocation.setValue('reserve_rm.config_id', meeting.room.configId)
  roomAllocation.setValue(
    'reserve_rm.rm_arrange_type_id',
    meeting.room.rmArrangeTypeId
  )
  roomAllocation.setValue('reserve_rm.date_start', startDate)
  roomAllocation.setValue('reserve_rm.date_end', endDate)
  roomAllocation.setValue(
    'reserve_rm.time_start',
    meeting.startTime + '.00.000'
  )
  roomAllocation.setValue('reserve_rm.time_end', meeting.endTime + '.00.000')
  roomAllocation.setValue(
    'reserve_rm.attendees_in_room',
    meeting.attendeesInRoom
  )

  roomAllocation.setValue('reserve_rm.status', meeting.status)

  let reservationExceptions = new Ab.data.DataSetList()
  let roomAllocationExceptions = new Ab.data.DataSetList()

  if (meeting.exceptions.length > 0) {
    meeting.exceptions.forEach(function(exception) {
      // clone the records
      let reservationException = new Ab.data.Record()
      reservationException.isNew = reservation.isNew
      copyValues(reservation, reservationException)

      let roomAllocationException = new Ab.data.Record()
      roomAllocationException.isNew = roomAllocation.isNew
      copyValues(roomAllocation, roomAllocationException)

      reservationException.setValue(
        'reserve.date_start',
        formatDate(exception.date, 'YYYY-MM-DD')
      )
      reservationException.setValue(
        'reserve.date_end',
        formatDate(exception.date, 'YYYY-MM-DD')
      )
      reservationException.setValue(
        'reserve.time_start',
        exception.start + '.00.000'
      )
      reservationException.setValue(
        'reserve.time_end',
        exception.end + '.00.000'
      )

      reservationException.setValue(
        'reserve.occurrence_index',
        exception.index + 1
      )

      roomAllocationException.setValue(
        'reserve_rm.date_start',
        formatDate(exception.date, 'YYYY-MM-DD')
      )
      roomAllocationException.setValue(
        'reserve_rm.date_end',
        formatDate(exception.date, 'YYYY-MM-DD')
      )
      roomAllocationException.setValue(
        'reserve_rm.time_start',
        exception.start + '.00.000'
      )
      roomAllocationException.setValue(
        'reserve_rm.time_end',
        exception.end + '.00.000'
      )

      reservationExceptions.addRecord(reservationException)
      roomAllocationExceptions.addRecord(roomAllocationException)
    })
  }

  return AbWorkflow.callMethod(
    'AbEssentialFacility-ReservationKioskService-saveRoomReservation',
    [
      reservation,
      roomAllocation,
      reservationExceptions,
      roomAllocationExceptions
    ]
  )
    .then(({code, message}) => {
      if (code === 'executed') {
        ReactGA.event({
          category: 'Meeting Reservation',
          action: 'Create Meeting Reservation'
        })
        return Promise.resolve()
      } else {
        return Promise.reject(message)
      }
    })
    .catch(message => {
      return Promise.reject(message)
    })
}

export const fetchMeetingRoomFreeBusy = async ({room}) => {
  const {blId, flId, rmId} = room
  let restriction = new Ab.view.Restriction()

  restriction.addClause('reserve_rm.bl_id', blId, '=', ')AND(')
  restriction.addClause('reserve_rm.fl_id', flId)
  if (rmId) {
    restriction.addClause('reserve_rm.rm_id', rmId)
  }

  const {data} = await DataV2.getDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'conferenceRoomReservations',
    restriction
  )
  return data.map(
    ({
      reserveResId,
      rmResId,
      reserveDateStart,
      reserveTimeStart,
      reserveTimeEnd
    }) => ({
      resId: reserveResId,
      rmResId: rmResId,
      start: combineDateAndTimeStrings(reserveDateStart, reserveTimeStart),
      end: combineDateAndTimeStrings(reserveDateStart, reserveTimeEnd)
    })
  )
}

const findRecurrences = reservation => {
  const {reserveResParent, reserveResId, reserveResType} = reservation
  const parentId = reserveResParent || reserveResId

  let restriction = new Ab.view.Restriction()
  restriction.addClause('reserve.res_parent', parentId)
  restriction.addClause('reserve.res_id', parentId, '=', 'OR')

  let result = {
    occurrences: [],
    dateList: [],
    exceptions: []
  }

  return DataV2.getDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'conferenceRoomReservations',
    restriction
  ).then(({data}) => {
    result.dateList = data.map(({reserveDateStart, reserveResId}) => ({
      date: reserveDateStart,
      resId: reserveResId
    }))

    if (reserveResType === 'regular') {
      result.occurrences = data.map(
        ({
          reserveResId,
          rmResId,
          reserveOccurrenceIndex,
          reserveDateStart,
          reserveTimeStart,
          reserveTimeEnd
        }) => ({
          resId: reserveResId,
          rmResId: rmResId,
          occurrenceIndex: reserveOccurrenceIndex,
          startDate: sqlDateToDate(reserveDateStart),
          startTime: formatTime(reserveTimeStart, 'HH:mm'),
          endTime: formatTime(reserveTimeEnd, 'HH:mm')
        })
      )
    } else {
      result.occurrences = data.reduce(
        (
          acc,
          {
            reserveResId,
            rmResId,
            reserveOccurrenceIndex,
            reserveDateStart,
            reserveTimeStart,
            reserveTimeEnd
          }
        ) => {
          if (reserveOccurrenceIndex !== 0) {
            acc.push({
              resId: reserveResId,
              rmResId: rmResId,
              occurrenceIndex: reserveOccurrenceIndex,
              startDate: sqlDateToDate(reserveDateStart),
              startTime: formatTime(reserveTimeStart, 'HH:mm'),
              endTime: formatTime(reserveTimeEnd, 'HH:mm')
            })
          }
          return acc
        },
        []
      )

      result.exceptions = data.reduce(
        (
          acc,
          {
            reserveResId,
            rmResId,
            reserveOccurrenceIndex,
            reserveDateStart,
            reserveTimeStart,
            reserveTimeEnd
          }
        ) => {
          if (reserveOccurrenceIndex === 0) {
            acc.push({
              resId: reserveResId,
              rmResId: rmResId,
              occurrenceIndex: reserveOccurrenceIndex,
              startDate: sqlDateToDate(reserveDateStart),
              startTime: formatTime(reserveTimeStart, 'HH:mm'),
              endTime: formatTime(reserveTimeEnd, 'HH:mm')
            })
          }
          return acc
        },
        []
      )
    }

    return Promise.resolve({...reservation, ...result})
  })
}

const findRoomResources = item => {
  const {blId, flId, rmId, configId, rmArrangeTypeId} = item
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm_resource_std.bl_id', blId)
  restriction.addClause('rm_resource_std.fl_id', flId)
  restriction.addClause('rm_resource_std.rm_id', rmId)
  if (configId) {
    restriction.addClause('rm_resource_std.config_id', configId)
  }
  if (rmArrangeTypeId) {
    restriction.addClause('rm_resource_std.rm_arrange_type_id', rmArrangeTypeId)
  }

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomResources',
    restriction
  ).then(({data}) => {
    let roomResources = data.map(resource => ({
      resourceStd: resource.resorceStd,
      name: resource.resourceName
    }))

    return Promise.resolve({...item, resources: roomResources})
  })
}

export const fetchRoomReservation = reservation => {
  reservation.attendeeList = []
  if (reservation.attendees && reservation.attendees !== '') {
    reservation.attendeeList = reservation.attendees.split(',').map(item => {
      return {
        name: item,
        email: item,
        em_title: ''
      }
    })
  }

  return getRoomPhoto(reservation)
    .then(reservation => {
      return findRoomResources(reservation)
    })
    .then(reservation => {
      return findRecurrences(reservation)
    })
    .then(reservation => {
      return Promise.resolve(reservation)
    })
}

export const cancelRoomReservation = ({
  resId,
  comments = '',
  cancelMeeting = true
}) => {
  return AbWorkflow.callMethod(
    'AbEssentialFacility-roomReservationService-cancelRoomReservation',
    [resId, comments, cancelMeeting]
  ).then(({code, message}) => {
    if (code === 'executed') {
      return Promise.resolve()
    } else {
      return Promise.resolve(message)
    }
  })
}

const reservationDefaultParams = ({blId, params}) => {
  let currentDate = formatDate(new Date(), 'YYYY-MM-DD')

  let defaultParams = {
    date_start: currentDate,
    date_end: currentDate
  }

  if (blId) {
    defaultParams = {
      bl_id: blId,
      fl_id: '',
      date_start: currentDate,
      date_end: currentDate,
      time_start: '',
      time_end: '',
      capacity: 0
    }
  }

  return Object.assign({}, defaultParams, params)
}

const getCapacityByFloor = ({params}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm_arrange.bl_id', params.bl_id)

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'capacityByFloor',
    restriction
  ).then(({data}) => {
    let indexedCapacity = data.reduce((acc, item) => {
      const {flId, maxCapacity} = item
      acc[`${flId}`] = maxCapacity
      return acc
    }, {})
    return Promise.resolve(indexedCapacity)
  })
}

const getBookingTimeRangeByFloor = async ({params}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm_arrange.bl_id', params.bl_id)

  const {data} = await DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomTimesByFloor',
    restriction
  )

  return data.reduce((acc, item) => {
    const {flId, timeStart, timeEnd} = item
    acc[`${flId}`] = {
      timeStart: parseTime({time:timeStart}),
      timeEnd: parseTime({time: timeEnd})
    }
    return acc
  }, {})

}

const getAvailableMeetingRoomsByFloor = availableRooms =>
  availableRooms.reduce((acc, curVal) => {
    const {bl_id, fl_id, rm_id} = curVal
    if (acc[fl_id]) {
      acc[fl_id].roomCodes.push({bl_id, fl_id, rm_id})
      acc[fl_id].avail_space += 1
    } else {
      acc[fl_id] = {
        roomCodes: [{bl_id, fl_id, rm_id}],
        avail_space: 1
      }
    }
    return acc
  }, {})

export const fetchAvailableRoomReservations = async ({
  blId = '',
  params = {}
}) => {
  let reservationParams = reservationDefaultParams({blId, params})

  let availableRooms = []

  const capacityByFloor = await getCapacityByFloor({params: reservationParams})

  const bookingRangeByFloor = await getBookingTimeRangeByFloor({
    params: reservationParams
  })

  const {code, data, message} = await AbWorkflow.callMethod(
    'AbEssentialFacility-ReservationKioskService-searchAvailableReservationRooms',
    [reservationParams]
  )

  if (code === 'executed') {
    availableRooms = getAvailableMeetingRoomsByFloor(data)
  } else {
    throw Error(message)
  }

  return {
    availableRooms,
    capacityByFloor,
    bookingRangeByFloor
  }
}

export const cancelRecurringRoomReservation = ({
  resId,
  comments,
  cancelMeeting
}) => {
  return AbWorkflow.callMethod(
    'AbEssentialFacility-roomReservationService-cancelRecurringRoomReservation',
    [resId, comments, cancelMeeting]
  )
    .then(({code, message}) => {
      if (code === 'executed') {
        ReactGA.event({
          category: 'Meeting Reservation',
          action: 'Cancel Meeting Reservation'
        })
        return Promise.resolve()
      } else {
        return Promise.reject(message)
      }
    })
    .catch(message => {
      return Promise.reject(message)
    })
}

/**
 * Fetches the number of Meeting and Workspace reservations and the first
 * Meeting reservation
 * @param emId
 */
export const fetchCombinedReservations = async ({emId}) => {
  const {
    data: meetingData,
    total: meetingTotal
  } = await fetchMeetingRoomReservations({
    employee: emId,
    numberOfRecords: 1
  })

  const {total: workspaceTotal} = await fetchWorkspaceReservations({
    emId,
    numberOfRecords: 1
  })

  const [meetingReservation] = meetingData

  return {
    totalNumberOfMeetingReservations: meetingTotal || 0,
    totalNumberOfWorkspaceReservations: workspaceTotal || 0,
    meetingReservation
  }
}
